import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import type { LicenseRequirement } from "@services/facilityCompliance";
import { formatLicenseFee } from "@utils/formatUtils";

const licensingTableHeaderSx = {
  fontSize: "11px",
  fontWeight: 700,
  color: "#FFF",
  fontFamily: "Rethink Sans, sans-serif",
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  borderBottom: "1px solid #0a6a80",
  padding: "10px 12px",
  backgroundColor: "#0a829d",
  verticalAlign: "top" as const,
  textAlign: "left" as const,
};

const licensingTableCellSx = {
  fontSize: "13px",
  fontFamily: "Rethink Sans, sans-serif",
  color: "#262626",
  borderBottom: "1px solid #f0f0f0",
  padding: "10px 12px",
  verticalAlign: "top" as const,
  textAlign: "left" as const,
};

export interface LicensingRequirementsTableProps {
  /** May be undefined when parallel compliance omits a jurisdiction tier. */
  licenses?: LicenseRequirement[] | null;
}

export const LicensingRequirementsTable: React.FC<
  LicensingRequirementsTableProps
> = ({ licenses }) => {
  const rows = licenses ?? [];
  if (!rows.length) {
    return null;
  }

  return (
    <Box
      sx={{
        border: "1px solid #0a829d",
        borderRadius: "4px",
        overflow: "hidden",
        mt: "4px",
      }}
    >
      <Box sx={{ overflowX: "auto" }}>
        <Table
          size="small"
          sx={{
            width: "100%",
            tableLayout: "fixed",
            "& .MuiTableCell-root": { verticalAlign: "top" },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  ...licensingTableHeaderSx,
                  width: "36%",
                  minWidth: 200,
                }}
              >
                REQUIREMENT
              </TableCell>
              <TableCell sx={licensingTableHeaderSx}>DESCRIPTION</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((license, index) => {
              const feeInfo = formatLicenseFee(license?.fee, license);
              return (
                <TableRow
                  key={index}
                  sx={{
                    "&:nth-of-type(even)": { backgroundColor: "#fafafa" },
                    "&:last-child td": { borderBottom: "none" },
                    "&:hover": { backgroundColor: "#f8fdfe" },
                  }}
                >
                  <TableCell
                    sx={{
                      ...licensingTableCellSx,
                      fontWeight: 600,
                      color: "#065b70",
                      wordBreak: "break-word",
                    }}
                  >
                    {license.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...licensingTableCellSx,
                      minWidth: 220,
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: "#fcfeff",
                        padding: "8px 12px",
                        borderRadius: "4px",
                        border: "1px solid #99d4e1",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "14px",
                          fontWeight: 600,
                          color: feeInfo.isUnknown ? "#6b7280" : "#065b70",
                          fontFamily: "Rethink Sans, sans-serif",
                          fontStyle: feeInfo.isUnknown ? "italic" : "normal",
                        }}
                      >
                        {feeInfo.displayText}
                      </Typography>
                      {feeInfo.workedExampleText && (
                        <Typography
                          sx={{
                            fontSize: "12px",
                            fontWeight: 500,
                            color: "#065b70",
                            fontFamily: "Rethink Sans, sans-serif",
                            marginTop: "4px",
                          }}
                        >
                          {feeInfo.workedExampleText}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
};
